import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  PhotoCamera as PhotoIcon,
  Description as DocumentIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { documentsAPI, apiCall } from '../services/api';

const DocumentUpload = ({ travelerId, travelerName, onDocumentChange }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [viewDialog, setViewDialog] = useState({ open: false, document: null, url: null });

  // Document types configuration
  const documentTypes = [
    { key: 'profile_photo', label: 'Profile Photo', accept: 'image/*', icon: PhotoIcon },
    { key: 'visa', label: 'Visa Document', accept: '.pdf,image/*', icon: DocumentIcon },
    { key: 'passport', label: 'Passport Copy', accept: '.pdf,image/*', icon: DocumentIcon },
    { key: 'medical', label: 'Medical Information', accept: '.pdf,image/*', icon: DocumentIcon },
  ];

  // Load documents on component mount
  React.useEffect(() => {
    if (travelerId) {
      fetchDocuments();
    }
  }, [travelerId]);

  const fetchDocuments = async () => {
    if (!travelerId) {
      setDocuments([]);
      return;
    }
    
    try {
      setLoading(true);
      const data = await apiCall(documentsAPI.getByTravelerId, travelerId);
      setDocuments(data.documents || []);
      if (onDocumentChange) onDocumentChange(data.documents || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (documentType, file) => {
    if (!file) return;

    // Check if travelerId is valid
    if (!travelerId) {
      setError('Please save the traveler first before uploading documents.');
      return;
    }

    // Validate file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
      setError(`File size (${fileSizeMB}MB) exceeds the 10MB limit. Please choose a smaller file.`);
      return;
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      setError(`File type "${file.type}" is not supported. Please upload JPEG, PNG, GIF, or PDF files only.`);
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(0);
      setError(null);

      const formData = new FormData();
      formData.append('document', file);
      formData.append('traveler_id', travelerId);
      formData.append('document_type', documentType);

      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 200);

      await apiCall(documentsAPI.upload, formData);
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      // Refresh documents list
      await fetchDocuments();
      
      setTimeout(() => {
        setUploadProgress(0);
      }, 1000);

    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelect = (documentType) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = getAcceptedTypes(documentType);
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        handleFileUpload(documentType, file);
      }
    };
    input.click();
  };

  const getAcceptedTypes = (documentType) => {
    const config = documentTypes.find(type => type.key === documentType);
    return config ? config.accept : '*';
  };

  const handleViewDocument = async (document) => {
    try {
      setError(null);
      
      const response = await documentsAPI.view(document.id);
      
      if (response.data) {
        const blob = response.data;
        const url = URL.createObjectURL(blob);
        setViewDialog({ open: true, document, url });
      } else {
        throw new Error('No data received from server');
      }
    } catch (err) {
      console.error('Error viewing document:', err);
      setError(`Failed to load document: ${err.response?.data?.error || err.message}`);
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    try {
      await apiCall(documentsAPI.delete, documentId);
      await fetchDocuments();
    } catch (err) {
      setError(err.message);
    }
  };


  const getDocumentIcon = (documentType) => {
    const config = documentTypes.find(type => type.key === documentType);
    return config ? config.icon : DocumentIcon;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getDocumentByType = (type) => {
    return documents.find(doc => doc.document_type === type);
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Documents for {travelerName || 'New Traveler'}
      </Typography>

      {!travelerId && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Save the traveler information first before uploading documents.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {uploading && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Uploading document... {uploadProgress}%
          </Typography>
          <LinearProgress variant="determinate" value={uploadProgress} />
        </Box>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : (
        <Box>
          {documentTypes.map((docType) => {
            const existingDoc = getDocumentByType(docType.key);
            const IconComponent = docType.icon;

            return (
              <Card key={docType.key} sx={{ mb: 2 }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box display="flex" alignItems="center" gap={2}>
                      <IconComponent color="primary" />
                      <Box>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {docType.label}
                        </Typography>
                        {existingDoc ? (
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              {existingDoc.original_filename}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatFileSize(existingDoc.file_size)} • 
                              Uploaded {new Date(existingDoc.upload_date).toLocaleDateString()}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No document uploaded
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    <Box display="flex" gap={1}>
                      {existingDoc ? (
                        <>
                          <Chip 
                            label="Uploaded" 
                            color="success" 
                            size="small" 
                            variant="outlined"
                          />
                          <IconButton
                            onClick={() => handleViewDocument(existingDoc)}
                            color="primary"
                            size="small"
                            aria-label={`View ${docType.label}`}
                            title={`View ${docType.label}`}
                          >
                            <ViewIcon />
                          </IconButton>
                          <IconButton
                            onClick={() => handleDeleteDocument(existingDoc.id)}
                            color="error"
                            size="small"
                            aria-label={`Delete ${docType.label}`}
                            title={`Delete ${docType.label}`}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </>
                      ) : (
                        <Button
                          type="button"
                          variant="outlined"
                          startIcon={<UploadIcon />}
                          onClick={() => handleFileSelect(docType.key)}
                          disabled={uploading || !travelerId}
                          size="small"
                          aria-label={`Upload ${docType.label}`}
                        >
                          Upload
                        </Button>
                      )}
                      
                      {existingDoc && (
                        <Button
                          type="button"
                          variant="outlined"
                          startIcon={<UploadIcon />}
                          onClick={() => handleFileSelect(docType.key)}
                          disabled={uploading || !travelerId}
                          size="small"
                          aria-label={`Replace ${docType.label}`}
                        >
                          Replace
                        </Button>
                      )}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}

      {/* Document Viewer Dialog */}
      <Dialog
        open={viewDialog.open}
        onClose={() => {
          if (viewDialog.url) {
            URL.revokeObjectURL(viewDialog.url);
          }
          setViewDialog({ open: false, document: null, url: null });
        }}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { 
            height: '90vh',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          pb: 1
        }}>
          <Typography variant="h6" component="div">
            {viewDialog.document?.original_filename}
          </Typography>
          <Chip 
            label={viewDialog.document?.document_type?.replace('_', ' ')?.toUpperCase()} 
            color="primary" 
            size="small"
            variant="outlined"
          />
        </DialogTitle>
        <DialogContent sx={{ p: 0, flex: 1, display: 'flex', flexDirection: 'column' }}>
          {viewDialog.url && viewDialog.document && (
            <Box sx={{ 
              flex: 1, 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              backgroundColor: '#f5f5f5',
              minHeight: 0
            }}>
              {viewDialog.document.original_filename.toLowerCase().endsWith('.pdf') ? (
                <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography variant="h6" sx={{ mb: 2 }}>PDF Document</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
                    PDF viewing in dialog is restricted by browser security.
                  </Typography>
                  <Button
                    type="button"
                    variant="contained"
                    onClick={() => {
                      const newWindow = window.open();
                      newWindow.document.write(`
                        <html>
                          <head><title>${viewDialog.document.original_filename}</title></head>
                          <body style="margin:0;">
                            <embed src="${viewDialog.url}" width="100%" height="100%" type="application/pdf">
                          </body>
                        </html>
                      `);
                    }}
                    sx={{ mb: 2 }}
                  >
                    Open PDF in New Tab
                  </Button>
                  <Typography variant="caption" color="text.secondary">
                    Click the button above to view the PDF document
                  </Typography>
                </Box>
              ) : (
                <img
                  src={viewDialog.url}
                  alt={viewDialog.document.original_filename}
                  style={{ 
                    maxWidth: '100%', 
                    maxHeight: '100%',
                    objectFit: 'contain',
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}
                />
              )}
            </Box>
          )}
          {!viewDialog.url && viewDialog.open && (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              height: '400px' 
            }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Loading document...</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          {viewDialog.document && (
            <Typography variant="body2" color="text.secondary" sx={{ mr: 'auto' }}>
              {formatFileSize(viewDialog.document.file_size)} • 
              Uploaded {new Date(viewDialog.document.upload_date).toLocaleDateString()}
            </Typography>
          )}
          <Button 
            type="button"
            onClick={() => {
              if (viewDialog.url) {
                URL.revokeObjectURL(viewDialog.url);
              }
              setViewDialog({ open: false, document: null, url: null });
            }}
            startIcon={<CancelIcon />}
            variant="outlined"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DocumentUpload;